<template>
  <NCarousel
    v-if="items?.length > 1"
    ref="carouselRef"
    class="box-content"
    effect="card"
    next-slide-style="transform: translateX(50%) translateZ(-800px);"
    prev-slide-style="transform: translateX(-150%) translateZ(-800px);"
    show-arrow
    :show-dots="false"
    :style="{
      width: `calc(100% - (${style?.paddingLeft ?? '0'} + ${style?.paddingRight ?? '0'}))`,
      height: isDownloadButtonVisible ? '325px' : '300px',
    }"
  >
    <NCarouselItem v-for="(item, index) in items" :key="index" :style="{ width: '256px' }">
      <ContentGridItem :all-items="items" :index="index" :item="item" :used-in-section-id="usedInSectionId" />
    </NCarouselItem>

    <template #arrow="{ currentIndex, next, prev, total }">
      <div
        class="content-grid-carousel__arrows"
        :style="{
          bottom: isDownloadButtonVisible
            ? `calc(10px + ${style?.paddingBottom ?? '0px'})`
            : `calc(25px + ${style?.paddingBottom ?? '0px'})`,

          fontFamily: 'var(--font-family)',
        }"
      >
        <button class="content-grid-carousel__arrow" type="button" @click="prev">
          <CIcon icon="angle-left" size="16px" />
        </button>
        <span>{{ currentIndex + 1 }} / {{ total }}</span>
        <button class="content-grid-carousel__arrow" type="button" @click="next">
          <CIcon icon="angle-right" size="16px" />
        </button>
      </div>
    </template>
  </NCarousel>
  <div v-else class="flex flex-col items-center pb-2">
    <ContentGridItem :all-items="items" :index="0" :item="items[0]" :used-in-section-id="usedInSectionId" />
  </div>
</template>
<script lang="ts" setup>
import { computed, ref, useAttrs, watch, type CSSProperties } from 'vue'
import { NCarousel, NCarouselItem, type CarouselInst } from 'naive-ui'

import ContentGridItem from '@canvas-builder/components/ui/ContentGrid/ContentGridItem.vue'
import {
  CanvasBuilderMode,
  type ContentGridItemFileProps,
  type ContentGridItemSlideProps,
} from '@canvas-builder/types/canvas'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import useCanvas from '@lib/apps/canvas-builder/composables/useCanvas'
import useThemeVars from '@lib/composables/useThemeVars'
import type { CanvasRetrieve } from '@openapi'

const attrs = useAttrs()

const props = defineProps<{
  items: (ContentGridItemFileProps | ContentGridItemSlideProps)[]
  usedInSectionId?: CanvasRetrieve['id']
}>()

const carouselRef = ref<CarouselInst | null>(null)
const themeVars = useThemeVars()
const { mode, isDefaultDisplayMode } = useCanvas()

// same condition as in the 'ContentGridItem'
const isDownloadButtonVisible = computed(() => mode.value === CanvasBuilderMode.SHARING && isDefaultDisplayMode.value)
const style = computed(() => attrs.style as CSSProperties)

watch(
  () => props.items.length,
  (newLength, oldLength) => {
    // If there are any changes in the length of the items
    // navigate to first item
    if (newLength !== oldLength) {
      carouselRef.value?.to(0)
    }
  },
)
</script>
<style scoped>
:deep(.content-grid-carousel__arrows) {
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  width: 100%;
  position: absolute;
  left: 0;
  z-index: 1;
}

:deep(.content-grid-carousel__arrow) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  color: v-bind('themeVars.text');
  background-color: rgba(0, 0, 0, 0.03);
  cursor: pointer;
  transition: background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

:deep(.content-grid-carousel__button:hover) {
  background-color: rgba(0, 0, 0, 0.1);
}
</style>
